import React, { createContext, useContext, useEffect, useState } from "react";
import { DEFAULT_SERVICE_CONFIGURATION } from "../contstants";
import { ENABLE_OXFORD_SELL_ORDER_LIMIT, SERVICE_CONFIG, TOKEN_EXPIRATION_KEY, TOKEN_KEY } from "../config/variables";
import { GetServiceConfig } from "../api/config/GetServiceConfig";
import { getAccessToken } from "../authProvider";

const GlobalContext = createContext();

// Helper to check if user is authenticated
const isAuthenticated = () => {
  const mfaData = JSON.parse(sessionStorage.getItem("mfa_user_sub") || null);
  // Check for access token in memory
  if (getAccessToken() && mfaData === null) {
    return true;
  }

  // Check for refresh token (ph_rt) cookie as fallback
  const refreshCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('ph_rt='));

  return !!refreshCookie && mfaData === null;
};

export const GlobalProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [isAuth, setIsAuth] = useState(isAuthenticated());
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [pageTitle, setPageTitle] = useState(null);
  const [showSideMenu, setshowSideMenu] = useState(false);
  const [configs, setConfigs] = useState(DEFAULT_SERVICE_CONFIGURATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch configs if authenticated
  useEffect(() => {
    if (!isAuth) {
      setLoading(false);
      return;
    }

    const localConfig = localStorage.getItem(SERVICE_CONFIG);
    if (localConfig) {
      try {
        setConfigs(JSON.parse(localConfig));
        setLoading(false);
      } catch {
        // fallback to refetch if JSON parse fails
      }
    }

    const fetchConfigs = async () => {
      setLoading(true);
      try {
        const response = await GetServiceConfig();
        const configs =
          response.data.client_portal_modules || DEFAULT_SERVICE_CONFIGURATION;
        configs[ENABLE_OXFORD_SELL_ORDER_LIMIT] = response.data.enable_oxford_sell_order_limit || false;
        setConfigs(configs);
        localStorage.setItem(SERVICE_CONFIG, JSON.stringify(configs));
        setError(false);
      } catch (err) {
        console.error("Config fetch error:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, [isAuth]);

  // Check authentication status periodically
  useEffect(() => {
    const checkAuth = () => {
      setIsAuth(isAuthenticated());
    };

    // Check every 10 seconds
    const interval = setInterval(checkAuth, 10000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // eventlistener for storage changes (e.g., logout in another tab)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === TOKEN_KEY || event.key === TOKEN_EXPIRATION_KEY) {
        // redirect to login if token is removed
        if (!event.newValue) {
          setIsAuth(false);
          window.location.href = "/user/login";
        }
      }  
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <GlobalContext.Provider
      value={{
        company,
        setCompany,
        isAuth,
        setIsAuth,
        breadcrumbs,
        setBreadcrumbs,
        pageTitle,
        setPageTitle,
        showSideMenu,
        setshowSideMenu,
        configs,
        setConfigs,
        loading,
        setLoading,
        error,
        setError,
        allowedModules: Object.keys(configs).filter((key) => configs[key]),
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  }
  return context;
};
